/**
 * Adapptio Editor
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import {
	cmpDataEqual,
	CompileContext,
	createEmptyScope,
	createEventTrigger,
	createRenderFunctionFromModel,
	DESIGN_CONTEXT_READ_MODE,
	DesignContext,
	ICompileResult,
	importSchema,
	ISchemaComponentModel,
	ISchemaConstObject,
	ISchemaDataSourceModel,
	ISchemaExpressionModel,
	ISchemaImportExport,
	RUNTIME_CONTEXT_MODE,
	RuntimeContext,
	SchemaExpression,
	serializeIDTToYAML,
	TGetBlueprintSchemaModel,
	TGetBlueprintSchemaSpec,
	TSchemaConstObjectProps,
	TGenericModelNode,
	ISchemaValue,
	ISchemaValueModel,
	TScopeData,
	createSubScope,
	isSchemaConstMapModel
} from "@adapptio/lib-blueprint";
import { TGenericViewComponentDefinition } from "@adapptio/lib-components";
import { BlueprintView, createViewScope, TBlueprintViewSchema } from "@adapptio/lib-core";
import { debounce } from "@adapptio/lib-editor-ui";
import {
	addHook,
	createEventEmitter,
	emitEvent,
	eventsHandler,
	removeAllEventListeners,
	removeHook,
	UndoRedoManager
} from "@adapptio/lib-shared";
import { EditorRenderContext } from "../../../runtime/EditorRenderContext";
import { EditorRoutingManager } from "../../../runtime/EditorRoutingManager";
import { RuntimeService } from "../../../runtime/RuntimeService";
import { IErrorListGroup } from "../../../ui/components/ErrorList/ErrorListTypes";
import { ISelectionItem_Outlet, ISelectionItem_ParameterAny, VIEW_SELECTION_ITEM_TYPE, ViewEditSelection } from "./ViewEditSelection";

export interface IViewModelOpts {}

export enum VIEW_MODEL_MODE {
	EDIT,
	PREVIEW,
	PREVIEW_EDIT
}

/**
 * View Documents
 */
export class ViewModel {
	/* Contexts */
	private renderCtx: EditorRenderContext<TBlueprintViewSchema>;
	private dCtx: DesignContext;
	private rCtx: RuntimeContext;
	private paramsDesignCtx: DesignContext; // used to create model from params schema (ISchemaImportExport)
	// private paramsRuntimeCtx: RuntimeContext; // used to render params spec from model

	/** Routing manager */
	private routingManager: EditorRoutingManager;

	/** Selection */
	private selection: ViewEditSelection;

	/** Schema expression model */
	public expresionModel: ISchemaExpressionModel;

	/** Emitted when runtime context has rendered */
	public onRender = createEventEmitter<void>();

	/** Emitted when runtime errors has changed */
	public onRuntimeErrors = createEventEmitter<void>();

	/** Emitted when parse errors has changed */
	public onParseErrors = createEventEmitter<void>();

	/** Emitted when blueprint model has changed */
	public onModelChange = createEventEmitter<void>();

	/** Emitted when new params model has been built */
	public onParamsModelBuilt = createEventEmitter<void>();

	/** Emitted when params model has changed */
	public onParamsModelChange = createEventEmitter<void>();

	/** Blueprint model */
	private model: TGetBlueprintSchemaModel<TBlueprintViewSchema> = null;

	/** If render was requested */
	private renderRequested = false;

	/** Rendered model spec */
	private modelSpec: TGetBlueprintSchemaSpec<TBlueprintViewSchema> = null;

	/** Undo/Redo manager */
	public undoRedoManager = new UndoRedoManager({ historyLimit: 10 });
	private lastSnapshot = null;

	/**
	 * Last exported params data - to detect params change
	 *
	 * - used to cache last params schema - ISchemaImportExport
	 * - params model should be rebuilt only when params schema differs from this prop = has changed
	 */
	private lastParamsSchemaData: ISchemaImportExport = null;

	/** Action parameters model */
	private paramsModel: ISchemaValueModel<ISchemaConstObject<TSchemaConstObjectProps>> = null;
	private paramsModelError?: Error;
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	private paramsData?: any;

	private readonly eventsHandler = eventsHandler();

	/**
	 * Document constructor
	 */
	public constructor(private runtimeSvc: RuntimeService, private opts: IViewModelOpts) {
		this.routingManager = runtimeSvc.createRoutingManager();
		this.selection = new ViewEditSelection();

		this.renderCtx = runtimeSvc.createRenderContext({
			mode: RUNTIME_CONTEXT_MODE.EDITOR,
			mediaResolution: "screenBase",
			routingManager: this.routingManager,
			getComponentListSpec: (spec) => spec?.spec?.content,
			eventLogFn: this.logComponentEvent
		});

		this.dCtx = new DesignContext({
			resolvers: this.renderCtx.getResolvers(),
			readMode: DESIGN_CONTEXT_READ_MODE.FULL,
			enableCompletition: true
		});

		this.paramsDesignCtx = new DesignContext({
			resolvers: {},
			readMode: DESIGN_CONTEXT_READ_MODE.SCAN,
			enableCompletition: true
		});

		this.rCtx = this.renderCtx.getRuntimeContext();

		const expressionSchema = SchemaExpression({});

		this.expresionModel = expressionSchema.createDefault(this.dCtx, null);

		this.eventsHandler.subscribe(this.dCtx.modelChangeEvent, () => {
			this.createSnapshot();
			this.handleModelChange();
		});

		this.eventsHandler.subscribe(this.rCtx.renderEvent, (spec) => {
			this.modelSpec = spec;
			this.buildParamsModel();
			this.expresionModel.schema.render(
				this.rCtx,
				this.expresionModel,
				[ "$" ],
				this.rCtx.getLastScope()
			);
			emitEvent(this.onRender);
		});

		this.eventsHandler.subscribe(this.rCtx.errorEvent, () => {
			emitEvent(this.onRuntimeErrors);
		});

		this.eventsHandler.subscribe(this.renderCtx.getGlobalsContainer().onChange, () => {
			this.updateScope(true);
		});

		this.eventsHandler.subscribe(this.paramsDesignCtx.modelChangeEvent, () => {
			this.createSnapshot();
			this.injectTestParams();
			emitEvent(this.onParamsModelChange);
		});

		this.handleBeforeNavigateHook = this.handleBeforeNavigateHook.bind(this);
		addHook(this.routingManager.beforeNavigateHook, this.handleBeforeNavigateHook);
	}

	private async initModel(): Promise<void> {
		const renderFn = createRenderFunctionFromModel(this.model);
		this.rCtx.setRenderFunction(renderFn, false);

		this.createSnapshot();
		this.updateScope(false);
		this.handleModelChange();

		await this.rCtx.waitForPendingOperations(false);
	}

	/**
	 * Creates an empty view blueprint
	 */
	public async createDefault(): Promise<void> {
		this.model = BlueprintView.createDefault(this.dCtx, null);
		await this.dCtx.waitForPendingOperations();
		await this.initModel();
	}

	/**
	 * Loads document
	 *
	 * @param blueprintContent Blueprint YAML contents
	 */
	public async loadDocument(blueprintContent: string): Promise<void> {
		this.dCtx.clearAllParseErrors();

		this.model = this.dCtx.loadYAMLDocument(BlueprintView, blueprintContent, "local://view");
		await this.dCtx.waitForPendingOperations();

		emitEvent(this.onParseErrors);

		await this.initModel();
	}

	/**
	 * Returns document as YAML
	 */
	public exportDocumentAsYAML(): string {
		if (!this.model) {
			throw new Error("Model is not initialized.");
		}

		const idt = this.model.schema.serialize(this.model, [ "$" ]);
		return serializeIDTToYAML(idt);
	}

	/**
	 * Compiles model to native code
	 */
	public compile(): ICompileResult {
		if (!this.model) {
			throw new Error("Model is not initialized.");
		}

		const cCtx = new CompileContext({
			resolvers: this.renderCtx.getResolvers()
		});

		return cCtx.compileModel(this.model);
	}

	public getExpressionModel() {
		return this.expresionModel;
	}

	/**
	 * Returns design context
	 */
	public getDesignContext(): DesignContext {
		return this.dCtx;
	}

	/**
	 * Returns runtime context
	 */
	public getRuntimeContext(): RuntimeContext<TBlueprintViewSchema> {
		return this.rCtx;
	}

	/**
	 * Returns render context
	 */
	public getRenderContext(): EditorRenderContext<TBlueprintViewSchema> {
		return this.renderCtx;
	}

	/**
	 * Returns last rendered spec (or null)
	 */
	public getLastSpec(): TGetBlueprintSchemaSpec<TBlueprintViewSchema> {
		return this.modelSpec;
	}

	/**
	 * Returns blueprint model
	 */
	public getModel(): TGetBlueprintSchemaModel<TBlueprintViewSchema> {
		return this.model;
	}

	/**
	 * Returns editor selection instance
	 */
	public getSelection(): ViewEditSelection {
		return this.selection;
	}

	/**
	 * Returns routing manager
	 */
	public getRoutingManager(): EditorRoutingManager {
		return this.routingManager;
	}

	/**
	 * Returns a model node by ID
	 *
	 * @param modelNodeId Model node ID
	 */
	public getModelNodeById(modelNodeId: number): TGenericModelNode {
		return this.dCtx.getNodeById(modelNodeId);
	}

	/**
	 * Returns a list of available components
	 */
	public getComponentList(): TGenericViewComponentDefinition[] {
		return this.runtimeSvc.getComponentRegistry().getItemList();
	}

	public getRenderContextMode(): RUNTIME_CONTEXT_MODE {
		return this.renderCtx.mode;
	}

	public getMode(): VIEW_MODEL_MODE {
		const renderCtxMode = this.renderCtx.mode;
		const runtimeCtxMode = this.rCtx.getMode();

		if (renderCtxMode === RUNTIME_CONTEXT_MODE.EDITOR) {
			if (runtimeCtxMode === RUNTIME_CONTEXT_MODE.EDITOR) {
				return VIEW_MODEL_MODE.EDIT;
			}

			return VIEW_MODEL_MODE.PREVIEW_EDIT;
		}

		return VIEW_MODEL_MODE.PREVIEW;
	}

	public setMode(mode: VIEW_MODEL_MODE): void {
		let renderCtxMode: RUNTIME_CONTEXT_MODE;
		let runtimeCtxMode: RUNTIME_CONTEXT_MODE;

		switch (mode) {
			case this.getMode():
				return;
			case VIEW_MODEL_MODE.PREVIEW:
				renderCtxMode = RUNTIME_CONTEXT_MODE.NORMAL;
				runtimeCtxMode = RUNTIME_CONTEXT_MODE.NORMAL;
				break;
			case VIEW_MODEL_MODE.EDIT:
				renderCtxMode = RUNTIME_CONTEXT_MODE.EDITOR;
				runtimeCtxMode = RUNTIME_CONTEXT_MODE.EDITOR;
				break;
			default:
				return;
		}

		this.renderCtx.setMode(renderCtxMode);
		this.rCtx.setMode(runtimeCtxMode, true); // trigger must be true otherwise react part will be desynchronized from spec
		this.renderModel();
	}

	/**
	 * Returns all errors in a format for error console
	 */
	public getErrors(): IErrorListGroup {
		return {
			documentUri: null,
			parseErrors: this.dCtx.getParseDocumentErrors("local://view"),
			runtimeErrors: this.rCtx.getRuntimeErrors(true),
			compileErrors: []
		};
	}

	/**
	 * Returns a component definition by name
	 *
	 * @param name Component name
	 */
	public getComponentDefinition(name: string): TGenericViewComponentDefinition {
		return this.runtimeSvc.getComponentRegistry().get(name);
	}

	private logComponentEvent = (eventType, opts, metaData, result) => {
		if (window.APP_DEBUG) {
			// eslint-disable-next-line max-len
			console.groupCollapsed(
				"[EventSystem] Handled event '%s' #%d node ID '%s' (%s)",
				metaData.eventName,
				metaData.executionOrder,
				metaData.nodeId,
				eventType
			);
			console.log("Options:", opts);
			console.log("Result:", result);
			console.log("Sender:", {
				id: metaData.senderId,
				path: metaData.senderPath,
				modelNodeId: metaData.senderModelNodeId,
				instance: metaData.senderInstance
			});
			console.log("Input Scope:", metaData.inputScope);
			console.groupEnd();
		}
	};

	private updateScope(triggerRender: boolean) {
		const scope = createViewScope(this.rCtx, {
			...(this.paramsData ?? {})
		});

		this.rCtx.setScope(scope, triggerRender);
	}

	private undoRedoHandle(snapshot) {
		this.model = BlueprintView.parse(this.dCtx, snapshot);
		const renderFn = createRenderFunctionFromModel(this.model);
		this.rCtx.setRenderFunction(renderFn, false);
		this.updateScope(true);
		this.buildParamsModel();
		this.renderModel();
		this.lastSnapshot = snapshot;
		this.selection.deselectAll();

		emitEvent(this.onModelChange);
		emitEvent(this.onParamsModelChange);
		emitEvent(this.onRender);
	}

	private createSnapshot = debounce(() => {
		const prevSnapshot = this.lastSnapshot;
		const currentSnapshot = this.model.schema.serialize(this.model, [ "$" ]);
		this.lastSnapshot = currentSnapshot;

		if (prevSnapshot) {
			this.undoRedoManager.add({
				undo: () => this.undoRedoHandle(prevSnapshot),
				redo: () => this.undoRedoHandle(currentSnapshot),
				disposeUndo: () => {
					/** */
				},
				disposeRedo: () => {
					/** */
				}
			});
			emitEvent(this.onRender);
			emitEvent(this.onParamsModelChange);
		}
	}, 500);

	private handleModelChange() {
		// Render
		this.renderModel();

		// Check selection
		this.checkSelection();
		emitEvent(this.onModelChange);
	}

	private renderModel() {
		// console.log("Model render requested.");

		if (this.renderRequested) {
			return;
		}

		this.renderRequested = true;

		setTimeout(() => {
			this.renderRequested = false;

			this.rCtx.clearAllRuntimeErrors();
			this.rCtx.render();
		}, 1);
	}

	private checkSelection() {
		const items = this.selection.getItems().slice();
		const events = this.selection.getEventNodes();

		const maxLen = Math.max(items.length, events.length);

		for (let i = 0; i < maxLen; i++) {
			if (items[i] && !this.dCtx.getNodeById(items[i].nodeId)) {
				this.selection.deselectByNodeId(items[i].nodeId);
			}
			if (events[i] && !this.dCtx.getNodeById(events[i].nodeId)) {
				this.selection.deselectEventNodeById(events[i].nodeId);
			}
		}
	}

	/**
	 * Deletes selected model nodes
	 */
	public deleteSelection(): void {
		const items = this.selection.getItems();
		console.log("Deleting selection:", items);
		items.forEach((item) => {
			switch (item.type) {
				case VIEW_SELECTION_ITEM_TYPE.COMPONENT: {
					const cmpModel = this.dCtx.getNodeById(item.nodeId) as ISchemaComponentModel;

					if (cmpModel) {
						cmpModel.schema.removeSelfFromComponentList(cmpModel, true, true);
					}

					break;
				}

				case VIEW_SELECTION_ITEM_TYPE.DATASOURCE: {
					const dsModel = this.dCtx.getNodeById(item.nodeId) as ISchemaDataSourceModel;

					if (dsModel) {
						dsModel.schema.removeSelfFromDataSourceList(dsModel, true, true);
					}

					break;
				}

				case VIEW_SELECTION_ITEM_TYPE.VIEW_PARAMETER_ANY: {
					const typedItem = item as ISelectionItem_ParameterAny;
					const parentModel = this.dCtx.getNodeById(typedItem.parentMapModelId);

					if (parentModel && isSchemaConstMapModel(parentModel)) {
						const index = parentModel.items.findIndex((i) => i === typedItem.mapEntry);

						if (index >= 0) {
							parentModel.schema.removeElement(parentModel, index, true);
						}
					}

					break;
				}

				case VIEW_SELECTION_ITEM_TYPE.VIEW_OUTLET: {
					const typedItem = item as ISelectionItem_Outlet;
					const parentModel = this.dCtx.getNodeById(typedItem.parentMapModelId);

					if (parentModel && isSchemaConstMapModel(parentModel)) {
						const index = parentModel.items.findIndex((i) => i === typedItem.mapEntry);

						if (index >= 0) {
							parentModel.schema.removeElement(parentModel, index, true);
						}
					}

					break;
				}
			}
		});
	}

	private buildParamsModel(): void {
		const paramsSchemaData = this.modelSpec.spec.params;

		// Prevent re-creation of params model if params spec hasn't changed
		if (this.lastParamsSchemaData && cmpDataEqual(paramsSchemaData, this.lastParamsSchemaData)) {
			return;
		}

		try {
			const paramsSchema = importSchema<ISchemaValue<ISchemaConstObject<TSchemaConstObjectProps>>>(paramsSchemaData);

			if (this.paramsModel) {
				const prevDataIdt = this.paramsModel.schema.serialize(this.paramsModel, [ "$" ]);
				this.paramsModel.schema.destroy(this.paramsModel);

				this.paramsModel = paramsSchema.parse(this.paramsDesignCtx, prevDataIdt, null);
			} else {
				this.paramsModel = paramsSchema.createDefault(this.paramsDesignCtx, null);
			}

			this.paramsModelError = undefined;
			this.injectTestParams();
		} catch (err) {
			this.paramsModelError = err;
		}

		this.lastParamsSchemaData = paramsSchemaData;

		emitEvent(this.onParamsModelBuilt);
		emitEvent(this.onParamsModelChange);
	}

	private async injectTestParams(): Promise<void> {
		if (!this.paramsModel) {
			return null;
		}

		const rCtx = new RuntimeContext(
			{
				mode: RUNTIME_CONTEXT_MODE.NORMAL,
				resolvers: {}
			},
			createRenderFunctionFromModel(this.paramsModel),
			createEmptyScope()
		);

		await rCtx.renderAsync();

		const paramsData = rCtx.getLastSpec();

		rCtx.destroy();

		this.paramsData = paramsData;
		this.updateScope(true);
	}

	public getParamsModel(): ISchemaValueModel<ISchemaConstObject<TSchemaConstObjectProps>> {
		return this.paramsModel;
	}

	/**
	 * Returns params model error if there was any
	 */
	public getParamsModelError(): Error | undefined {
		return this.paramsModelError;
	}

	public clearAllParseErrors(): void {
		this.dCtx.clearAllParseErrors();
		emitEvent(this.onParseErrors);
	}

	private async triggerViewEvent(eventName: string, localData?: TScopeData): Promise<unknown> {
		const eventSpec = this.rCtx.getLastSpec()?.spec?.events;
		const getEventSpec = (eventName: string) => this.rCtx.getLastSpec()?.spec?.events[eventName];

		if (eventSpec?.[eventName]) {
			const evTrigger = createEventTrigger(
				this.rCtx,
				"componentEvent",
				getEventSpec,
				eventName,
				"$",
				[ "$" ],
				-1,
				null,
				this.model.props.spec.value.props.events.value.props[eventName].props.nodes.template
			);

			const subScope = localData
				? createSubScope(this.rCtx.getLastScope(), localData)
				: this.rCtx.getLastScope();

			return await evTrigger(subScope);
		}
	}

	public async triggerOnInitEvent(): Promise<void> {
		await this.triggerViewEvent("onInit");
	}

	public async triggerOnParamsChangedEvent(): Promise<void> {
		await this.triggerViewEvent("onParamsChanged");
	}

	public async handleBeforeNavigateHook(path: string|number): Promise<boolean> {
		const res = await this.triggerViewEvent("onBeforeNavigate", {
			$path: path
		});

		return res === false ? false : true;
	}

	public dispose(): void {
		this.undoRedoManager.clear();
		this.routingManager.dispose();
		this.eventsHandler.unsubscribe();

		if (this.model) {
			this.model.schema.destroy(this.model);
		}

		if (this.paramsModel) {
			this.paramsModel.schema.destroy(this.paramsModel);
		}

		removeHook(this.routingManager.beforeNavigateHook, this.handleBeforeNavigateHook);

		this.dCtx.destroy();
		this.renderCtx.destroy();
		this.selection.destroy();

		removeAllEventListeners(this.onRuntimeErrors);
		removeAllEventListeners(this.onModelChange);
		removeAllEventListeners(this.onParamsModelChange);
		removeAllEventListeners(this.onParamsModelBuilt);
	}
}
